// TeamScheduleView.jsx - Focused view for a single team's schedule
import React, { useMemo, useState, useRef, useEffect, memo } from 'react';
import { MapPin, X } from 'lucide-react';
import logo from '../assets/images/LoginpageGIF9.gif';
const logoBlue = '/Pitchallo.svg';

const TeamScheduleView = ({ 
  clubName, 
  clubLogo, 
  teamName, 
  date, 
  allocations, 
  pitches,
  satelliteConfig,
  pitchConfigs,
  branding,
  pitchReferencePoints,
  onViewAllTeams,
  onBack 
}) => {

  const [selectedMatch, setSelectedMatch] = useState(null);
  const [mapView, setMapView] = useState('satellite'); // 'satellite' | 'pitch'

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Filter allocations for this specific team
  const teamMatches = useMemo(() => {
    const matches = [];

    Object.entries(allocations).forEach(([allocationId, allocation]) => {
      // Handle actual structure: team at top level
      if (allocation.team === teamName || allocation.teamName === teamName) {
        // Get ALL sections for this allocation
        const sectionsList = Array.isArray(allocation.sections) 
          ? allocation.sections 
          : (allocation.section ? [allocation.section] : []);
        
        // Create ONE match entry with ALL sections
        matches.push({
          id: allocationId,
          allocationId,
          startTime: allocation.startTime,
          endTime: allocation.endTime,
          duration: allocation.duration || 30,
          pitchId: allocation.pitchId,
          pitchNumber: allocation.pitchNumber,
          sections: sectionsList, // Store all sections as array
          opponent: allocation.opponent || null,
          notes: allocation.notes || null,
          allocationType: allocation.type || allocation.allocationType || 'training'
        });
      }
      // Also handle future structure: sections array with objects
      else if (allocation.sections && Array.isArray(allocation.sections)) {
        // Group sections by team within this allocation
        const teamSections = allocation.sections.filter(section => 
          typeof section === 'object' && section.team === teamName
        );
        
        if (teamSections.length > 0) {
          matches.push({
            id: allocationId,
            allocationId,
            startTime: allocation.startTime,
            endTime: allocation.endTime,
            duration: allocation.duration || 30,
            pitchId: allocation.pitchId,
            pitchNumber: allocation.pitchNumber,
            sections: teamSections.map(s => s.section), // Extract section letters
            opponent: teamSections[0].opponent || allocation.opponent || null,
            notes: allocation.notes || null,
            allocationType: allocation.type || allocation.allocationType || 'training'
          });
        }
      }
    });

    // GROUP matches that have the same time, pitch, and opponent
    // This handles the case where allocations are split into multiple documents
    const groupedMatches = [];
    const matchGroups = new Map();

    matches.forEach(match => {
      // Create a unique key for matches that should be grouped together
      const groupKey = `${match.startTime}-${match.endTime}-${match.pitchNumber}-${match.opponent || 'none'}`;
      
      if (matchGroups.has(groupKey)) {
        // Add sections to existing group
        const existing = matchGroups.get(groupKey);
        const existingSections = existing.sections || [];
        const newSections = match.sections || [];
        existing.sections = [...new Set([...existingSections, ...newSections])].sort();
      } else {
        // Create new group
        matchGroups.set(groupKey, { ...match, sections: match.sections || [] });
      }
    });

    // Convert map to array
    matchGroups.forEach(match => groupedMatches.push(match));

    // Sort by start time
    return groupedMatches.sort((a, b) => {
      if (!a.startTime || !b.startTime) return 0;
      return a.startTime.localeCompare(b.startTime);
    });
  }, [allocations, teamName]);

  const getPitchDisplayName = (pitchNumber) => {
    return `Pitch ${pitchNumber}`;
  };

  const getMatchTypeIcon = (type) => {
    switch(type) {
      case 'match':
      case 'game':
        return '🏆';
      case 'training':
        return '⚽';
      default:
        return '📅';
    }
  };

  if (teamMatches.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#243665'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '16px 20px',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <button
              onClick={onBack}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Empty State */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 200px)',
          padding: '20px'
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '400px'
          }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '16px'
            }}>🤔</div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '8px'
            }}>
              No matches found
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              marginBottom: '24px'
            }}>
              {teamName} doesn't have any scheduled matches for this date.
            </p>
            <button
              onClick={onViewAllTeams}
              style={{
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              View All Teams
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#243665'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 20px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <button
              onClick={onBack}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ← Back
            </button>
            
            {clubLogo && (
              <img 
                src={clubLogo} 
                alt={clubName}
                style={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'contain'
                }}
              />
            )}
            
            <div>
              <h1 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#111827',
                margin: 0,
                lineHeight: 1.2
              }}>
                {teamName}
              </h1>
              <p style={{
                fontSize: '13px',
                color: '#6b7280',
                margin: 0
              }}>
                {formatDate(date)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '24px 20px'
      }}>
        {/* Title Section */}
        <div style={{
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '600',
            color: '#ffffff',
            margin: '0 0 8px 0'
          }}>
            📍 Your Match Locations
          </h2>
          <p style={{
            fontSize: '15px',
            color: '#d1d5db',
            margin: 0
          }}>
            {teamMatches.length} {teamMatches.length === 1 ? 'match' : 'matches'} scheduled today
          </p>
        </div>

        {/* Matches List */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {teamMatches.map((match, index) => (
            <div
              key={match.id}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}
            >
              {/* Time and Type */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>
                  {getMatchTypeIcon(match.allocationType)}
                </span>
                <div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    {match.startTime && match.endTime 
                      ? `${formatTime(match.startTime)} - ${formatTime(match.endTime)}`
                      : match.startTime 
                        ? formatTime(match.startTime)
                        : 'Time TBA'
                    }
                  </div>
                  {match.opponent && (
                    <div style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      marginTop: '2px'
                    }}>
                      vs {match.opponent}
                    </div>
                  )}
                </div>
              </div>

              {/* Location */}
              <div style={{
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px'
              }}>
                <div style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '4px'
                }}>
                  {getPitchDisplayName(match.pitchNumber)}, {match.sections.length === 1 
                    ? `Section ${match.sections[0]}`
                    : `Sections ${match.sections.join(', ')}`}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  {clubName}
                </div>
              </div>

              {/* Notes */}
              {match.notes && (
                <div style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  marginBottom: '12px',
                  fontStyle: 'italic'
                }}>
                  Note: {match.notes}
                </div>
              )}

              {/* View on Map Button */}
              {satelliteConfig && (
                <button
                  onClick={() => setSelectedMatch(match)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <MapPin size={18} />
                  View on Map
                </button>
              )}
            </div>
          ))}
        </div>

        {/* View All Teams Button */}
        <button
          onClick={onViewAllTeams}
          style={{
            marginTop: '24px',
            width: '100%',
            padding: '16px',
            backgroundColor: 'white',
            border: '1.5px solid #e5e7eb',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '500',
            color: '#6b7280',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '18px' }}>📅</span>
          View All Teams This Day
        </button>

        {/* Map Modal - two stages: satellite → pitch sections */}
        {selectedMatch && satelliteConfig && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: mapView === 'satellite' ? 'rgba(0,0,0,0.95)' : '#f3f4f6',
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto'
          }}>
            {/* Satellite view header */}
            {mapView === 'satellite' && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                backgroundColor: 'rgba(0,0,0,0.8)',
                flexShrink: 0
              }}>
                <button
                  onClick={() => { setSelectedMatch(null); setMapView('satellite'); }}
                  style={{ padding: '8px 16px', backgroundColor: 'white', color: '#1f2937', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <X size={14} /> Close
                </button>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: 'white' }}>{teamName}</div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                    {formatTime(selectedMatch.startTime)} – {formatTime(selectedMatch.endTime)} · {getPitchDisplayName(selectedMatch.pitchNumber)}
                  </div>
                </div>
                <div style={{ width: '80px' }} />
              </div>
            )}

            {/* Satellite view - click pitch to enter section view */}
            {mapView === 'satellite' && (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px',
                gap: '16px'
              }}>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>
                  Tap your pitch to see section allocations
                </p>
                <PitchMapCanvas
                  satelliteConfig={satelliteConfig}
                  pitches={pitches}
                  pitchNumber={selectedMatch.pitchNumber}
                  onPitchClick={() => setMapView('pitch')}
                />
              </div>
            )}

            {/* Pitch section view */}
            {mapView === 'pitch' && (
              <PitchSectionView
                date={date}
                pitchNumber={selectedMatch.pitchNumber}
                pitchName={getPitchDisplayName(selectedMatch.pitchNumber)}
                allocations={allocations}
                pitchConfigs={pitchConfigs}
                pitchReferencePoints={pitchReferencePoints}
                highlightTeam={teamName}
                startTime={selectedMatch.startTime}
                endTime={selectedMatch.endTime}
                branding={branding}
                clubName={clubName}
                clubLogo={clubLogo}
                onBack={() => setMapView('satellite')}
              />
            )}
          </div>
        )}

      </div>
    </div>
  );
};

// Renders the satellite image zoomed in on the correct pitch, using corners for accuracy
const PitchMapCanvas = memo(({ satelliteConfig, pitches, pitchNumber, onPitchClick }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Find the correct pitch data
  const pitchData = useMemo(() => {
    if (!pitches) return null;
    return pitches.find(p =>
      p.pitchNumber === pitchNumber ||
      p.pitchNumber === Number(pitchNumber) ||
      p.pitchNumber === pitchNumber?.toString() ||
      p.id === `pitch-${pitchNumber}`
    );
  }, [pitches, pitchNumber]);

  useEffect(() => {
    if (!imageLoaded || !canvasRef.current || !pitchData) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const image = imageRef.current;
    if (!image) return;

    const canvasSize = canvas.width;

    // Zoom in centred on the pitch with padding
    const padding = 1.6;
    const maxDim = Math.max(pitchData.width, pitchData.height) * padding;
    const srcX = Math.max(0, pitchData.centerX - maxDim / 2);
    const srcY = Math.max(0, pitchData.centerY - maxDim / 2);

    ctx.clearRect(0, 0, canvasSize, canvasSize);
    ctx.drawImage(image, srcX, srcY, maxDim, maxDim, 0, 0, canvasSize, canvasSize);

    const scale = canvasSize / maxDim;
    const corners = pitchData.corners;

    if (corners && corners.length === 4) {
      // Draw using corners (handles rotation implicitly)
      const scaled = corners.map(c => ({
        x: (c.x - srcX) * scale,
        y: (c.y - srcY) * scale
      }));

      ctx.beginPath();
      ctx.moveTo(scaled[0].x, scaled[0].y);
      scaled.slice(1).forEach(c => ctx.lineTo(c.x, c.y));
      ctx.closePath();

      ctx.fillStyle = 'rgba(34, 197, 94, 0.35)';
      ctx.fill();
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 4;
      ctx.stroke();

      // Label
      const lx = scaled.reduce((s, c) => s + c.x, 0) / 4;
      const ly = scaled.reduce((s, c) => s + c.y, 0) / 4;
      ctx.font = 'bold 22px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 4;
      ctx.strokeText(pitchData.name || `Pitch ${pitchNumber}`, lx, ly);
      ctx.fillStyle = '#fff';
      ctx.fillText(pitchData.name || `Pitch ${pitchNumber}`, lx, ly);

    } else {
      // Fallback: center/width/height/rotation
      const cx = (pitchData.centerX - srcX) * scale;
      const cy = (pitchData.centerY - srcY) * scale;
      const w = pitchData.width * scale;
      const h = pitchData.height * scale;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(pitchData.rotation || 0);
      ctx.fillStyle = 'rgba(34, 197, 94, 0.35)';
      ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 4;
      ctx.strokeRect(-w / 2, -h / 2, w, h);
      ctx.restore();

      ctx.font = 'bold 22px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 4;
      ctx.strokeText(pitchData.name || `Pitch ${pitchNumber}`, cx, cy);
      ctx.fillStyle = '#fff';
      ctx.fillText(pitchData.name || `Pitch ${pitchNumber}`, cx, cy);
    }

  }, [imageLoaded, pitchData, pitchNumber]);

  const imageUrl = satelliteConfig?.imageUrl || satelliteConfig?.satelliteImageUrl;

  if (!imageUrl) {
    return <div style={{ color: 'white' }}>No satellite image available</div>;
  }

  if (!pitchData) {
    return <div style={{ color: 'white' }}>Pitch location not configured</div>;
  }

  const size = Math.min(window.innerWidth - 40, window.innerHeight - 220, 700);

  return (
    <div style={{ cursor: onPitchClick ? 'pointer' : 'default' }} onClick={onPitchClick}>
      <img
        ref={imageRef}
        src={imageUrl}
        alt="satellite"
        crossOrigin="anonymous"
        onLoad={() => setImageLoaded(true)}
        style={{ display: 'none' }}
      />
      {!imageLoaded && (
        <div style={{ color: 'white', fontSize: '16px' }}>Loading map...</div>
      )}
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        style={{
          display: imageLoaded ? 'block' : 'none',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          maxWidth: '100%'
        }}
      />
      {onPitchClick && imageLoaded && (
        <div style={{
          textAlign: 'center',
          marginTop: '12px',
          color: 'rgba(255,255,255,0.8)',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px'
        }}>
          👆 Tap to view section allocations
        </div>
      )}
    </div>
  );
});

// ── Helper functions (mirrored from ShareView) ───────────────────────────────

const getSectionsForPitch = (config) => {
  if (!config) return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  if (config.sectionsCount) {
    if (config.sectionsCount === 8) return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    if (config.sectionsCount === 4) return ['A', 'B', 'C', 'D'];
    if (config.sectionsCount === 2) return ['A', 'C'];
    if (config.sectionsCount === 1) return ['A'];
  }
  if (config.sectionLayout) {
    if (config.sectionLayout === 'FULL' || config.sectionLayout === 'EIGHTH') return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    if (config.sectionLayout === 'HALF') return ['A', 'B', 'C', 'D'];
    if (config.sectionLayout === 'QUARTER') return ['A', 'C'];
    if (config.sectionLayout === 'SMALL') return ['A'];
  }
  return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
};

const getGridLayout = (config) => {
  if (!config) return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
  if (config.sectionsCount) {
    if (config.sectionsCount === 8) return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
    if (config.sectionsCount === 4) return { columns: '1fr 1fr', rows: '1fr 1fr' };
    if (config.sectionsCount === 2) return { columns: '1fr', rows: '1fr 1fr' };
    if (config.sectionsCount === 1) return { columns: '1fr', rows: '1fr' };
  }
  if (config.sectionLayout) {
    if (config.sectionLayout === 'FULL' || config.sectionLayout === 'EIGHTH') return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
    if (config.sectionLayout === 'HALF') return { columns: '1fr 1fr', rows: '1fr 1fr' };
    if (config.sectionLayout === 'QUARTER') return { columns: '1fr', rows: '1fr 1fr' };
    if (config.sectionLayout === 'SMALL') return { columns: '1fr', rows: '1fr' };
  }
  return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
};

const isLightColor = (hex) => {
  if (!hex) return false;
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 186;
};

// ── PitchSectionView ─────────────────────────────────────────────────────────
// Identical look to ShareView's pitch view - same header, logos, pitch renderer

const PitchSectionView = ({ date, pitchNumber, pitchName, allocations, pitchConfigs, pitchReferencePoints, highlightTeam, startTime, endTime, branding, clubName, clubLogo, onBack }) => {
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const isMobile = window.innerWidth <= 768;

  const pitchId = `pitch-${pitchNumber}`;
  const pitchConfig = pitchConfigs?.[pitchId] || pitchConfigs?.[`pitch${pitchNumber}`] || null;
  const sections = getSectionsForPitch(pitchConfig);
  const gridLayout = getGridLayout(pitchConfig);
  const referencePoints = pitchReferencePoints?.[pitchId] || null;

  // Branding
  const primaryColor = branding?.primaryColor || '#243665';
  const secondaryColor = branding?.secondaryColor || '#3b82f6';
  const clubLogoUrl = branding?.clubLogoUrl || clubLogo || null;
  const sponsorLogoUrl = branding?.sponsorLogoUrl || null;
  const rawSponsorUrl = branding?.sponsorUrl || '';
  const sponsorUrl = rawSponsorUrl && !rawSponsorUrl.startsWith('http') ? `https://${rawSponsorUrl}` : rawSponsorUrl;
  const textColor = isLightColor(primaryColor) ? '#000000' : '#ffffff';

  const fmtTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const getAllocationForSection = (section) => {
    const key = `${date}-${startTime}-${pitchId}-${section}`;
    if (allocations[key]) return allocations[key];
    return Object.values(allocations).find(a =>
      (a.team || a.teamName) &&
      Array.isArray(a.sections) && a.sections.includes(section) &&
      a.pitchId === pitchId &&
      a.startTime === startTime
    ) || null;
  };

  const renderPitchMarkings = () => {
    const count = pitchConfig?.sectionsCount || (pitchConfig?.sectionLayout === 'FULL' ? 8 : 8);
    if (count >= 8) return (
      <>
        <div style={{ position: 'absolute', top: 2, left: 2, right: 2, bottom: 2, border: '2px solid white', borderRadius: 4 }} />
        <div style={{ position: 'absolute', left: 2, right: 2, top: '50%', height: 2, backgroundColor: 'white', transform: 'translateY(-50%)' }} />
        <div style={{ position: 'absolute', left: '50%', top: '50%', width: 80, height: 80, border: '2px solid white', borderRadius: '50%', transform: 'translate(-50%,-50%)' }} />
        <div style={{ position: 'absolute', left: '50%', top: '50%', width: 4, height: 4, backgroundColor: 'white', borderRadius: '50%', transform: 'translate(-50%,-50%)' }} />
        <div style={{ position: 'absolute', top: 2, left: '25%', right: '25%', height: 60, border: '2px solid white', borderTop: 'none' }} />
        <div style={{ position: 'absolute', bottom: 2, left: '25%', right: '25%', height: 60, border: '2px solid white', borderBottom: 'none' }} />
      </>
    );
    if (count === 4) return (
      <>
        <div style={{ position: 'absolute', top: 2, left: 2, right: 2, bottom: 2, border: '2px solid white', borderRadius: 0 }} />
        <div style={{ position: 'absolute', left: '50%', top: 0, width: 80, height: 40, border: '2px solid white', borderRadius: '0 0 40px 40px', borderTop: 'none', transform: 'translateX(-50%)' }} />
      </>
    );
    return <div style={{ position: 'absolute', top: 2, left: 2, right: 2, bottom: 2, border: '2px solid white', borderRadius: 4 }} />;
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', padding: '0' }}>

      {/* ── Top Banner: Pitchallo Logo + Back Button ── */}
      <div style={{
        backgroundColor: '#243665',
        padding: isMobile ? '8px 16px' : '12px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <button
          onClick={onBack}
          style={{ 
            padding: '8px 16px', 
            backgroundColor: 'rgba(255,255,255,0.2)', 
            color: '#ffffff', 
            border: '2px solid rgba(255,255,255,0.5)', 
            borderRadius: '6px', 
            cursor: 'pointer', 
            fontSize: '14px', 
            fontWeight: '600' 
          }}
        >
          ← Back to Map
        </button>
        
        <img 
          src={logoBlue} 
          alt="Pitchallo" 
          style={{ 
            height: isMobile ? '120px' : '150px', 
            width: 'auto' 
          }} 
        />
      </div>

      {/* ── Pitch Content ── */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '16px 12px' : '24px 20px' }}>
        
        {/* Club Header Card */}
        <div style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          marginBottom: '20px',
          padding: isMobile ? '16px' : '24px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          {clubLogoUrl && (
            <img 
              src={clubLogoUrl} 
              alt="Club Logo" 
              loading="lazy" 
              style={{ 
                maxHeight: isMobile ? '80px' : '120px', 
                maxWidth: '300px', 
                width: 'auto', 
                height: 'auto', 
                objectFit: 'contain',
                marginBottom: '16px',
                display: 'block'
              }} 
              onError={e => e.currentTarget.style.display = 'none'} 
            />
          )}
          
          <h1 style={{ 
            fontSize: isMobile ? '24px' : '32px', 
            fontWeight: 'bold', 
            color: textColor, 
            margin: '0 0 8px 0'
          }}>
            {pitchName}
          </h1>
          <div style={{ 
            fontSize: isMobile ? '14px' : '16px', 
            color: textColor, 
            opacity: 0.9
          }}>
            {new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            {' · '}{fmtTime(startTime)} – {fmtTime(endTime)}
          </div>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: isMobile ? '16px' : '20px', paddingBottom: isMobile ? '40px' : '50px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600', color: '#1f2937', marginBottom: '32px', textAlign: 'center' }}>
            {fmtTime(startTime)} – {fmtTime(endTime)}
          </h3>

          {/* Pitch visual */}
          <div className="pitch-visual" style={{
            position: 'relative',
            backgroundColor: '#dcfce7',
            border: '4px solid white',
            borderRadius: '8px',
            padding: '16px',
            width: '280px',
            height: '400px',
            margin: '0 auto'
          }}>
            {/* Pitch Reference Points - Outside overflow container */}
            {referencePoints && (
              <>
                {referencePoints.top && (
                  <div style={{ position: 'absolute', top: '-28px', left: '50%', transform: 'translateX(-50%)', fontSize: '13px', fontWeight: '500', color: '#4b5563', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px', zIndex: 20 }}>
                    <span>{referencePoints.top}</span>
                    <span style={{ fontSize: '10px', transform: 'rotate(180deg)', display: 'inline-block' }}>▼</span>
                  </div>
                )}
                {referencePoints.bottom && (
                  <div style={{ position: 'absolute', bottom: '-28px', left: '50%', transform: 'translateX(-50%)', fontSize: '13px', fontWeight: '500', color: '#4b5563', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px', zIndex: 20 }}>
                    <span>{referencePoints.bottom}</span>
                    <span style={{ fontSize: '10px' }}>▼</span>
                  </div>
                )}
                {referencePoints.left && (
                  <div style={{ position: 'absolute', top: '50%', left: '-42px', transform: 'translateY(-50%)', fontSize: '13px', fontWeight: '500', color: '#4b5563', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', zIndex: 20 }}>
                    <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>{referencePoints.left}</span>
                    <span style={{ fontSize: '10px', transform: 'rotate(90deg)' }}>▼</span>
                  </div>
                )}
                {referencePoints.right && (
                  <div style={{ position: 'absolute', top: '50%', right: '-42px', transform: 'translateY(-50%)', fontSize: '13px', fontWeight: '500', color: '#4b5563', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', zIndex: 20 }}>
                    <span style={{ fontSize: '10px', transform: 'rotate(-90deg)' }}>▼</span>
                    <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>{referencePoints.right}</span>
                  </div>
                )}
              </>
            )}
            
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#bbf7d0', borderRadius: '8px', overflow: 'hidden' }}>
              {renderPitchMarkings()}
            </div>

            <div className="pitch-sections" style={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: gridLayout.columns,
              gridTemplateRows: gridLayout.rows,
              gap: '4px',
              height: '100%',
              zIndex: 10
            }}>
              {sections.map(sec => {
                const allocation = getAllocationForSection(sec);
                const teamName = allocation?.team || allocation?.teamName || null;
                const isHighlighted = teamName === highlightTeam;
                const teamColor = allocation?.colour || allocation?.color || allocation?.teamColor || '#3b82f6';
                const isMatch = allocation?.type === 'match' || allocation?.allocationType === 'match';

                let backgroundColor, borderColor, color;
                if (allocation) {
                  backgroundColor = isHighlighted ? teamColor + 'ee' : teamColor + '55';
                  borderColor = isHighlighted ? '#f59e0b' : teamColor + '99';
                  color = isHighlighted 
                    ? (isLightColor(teamColor) ? '#000' : '#fff')
                    : (isLightColor(teamColor) ? '#33333399' : '#ffffff99');
                } else {
                  backgroundColor = 'rgba(255,255,255,0.08)';
                  borderColor = 'rgba(255,255,255,0.3)';
                  color = '#37415166';
                }

                return (
                  <div
                    key={sec}
                    onClick={() => allocation && setSelectedAllocation({ ...allocation, section: sec, teamName, pitchId })}
                    title={allocation ? `${teamName} - Tap for details` : `Section ${sec} - Available`}
                    style={{
                      border: isHighlighted ? '3px solid #f59e0b' : `2px solid ${borderColor}`,
                      borderRadius: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: sections.length === 1 ? '20px' : sections.length === 2 ? '16px' : '12px',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      position: 'relative',
                      padding: sections.length === 1 ? '8px' : '2px',
                      textAlign: 'center',
                      cursor: allocation ? 'pointer' : 'default',
                      backgroundColor: isHighlighted ? teamColor + 'cc' : backgroundColor,
                      color,
                      boxShadow: isHighlighted ? 'inset 0 0 0 2px rgba(255,255,255,0.6)' : 'none'
                    }}
                  >
                    {/* M/T badge */}
                    {allocation && (
                      <div style={{
                        position: 'absolute', top: 2, right: 2,
                        width: sections.length === 1 ? '20px' : '14px',
                        height: sections.length === 1 ? '20px' : '14px',
                        backgroundColor: isMatch ? '#ef4444' : '#3b82f6',
                        color: 'white',
                        fontSize: sections.length === 1 ? '12px' : '8px',
                        fontWeight: 'bold',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '2px', zIndex: 20
                      }}>
                        {isMatch ? 'M' : 'T'}
                      </div>
                    )}

                    <div style={{ fontSize: sections.length === 1 ? '24px' : sections.length === 2 ? '16px' : '12px', opacity: 0.75, marginBottom: sections.length === 1 ? '8px' : '4px', fontWeight: 'bold' }}>
                      {sec}
                    </div>
                    <div style={{ textAlign: 'center', padding: '0 4px', fontSize: sections.length === 1 ? '18px' : sections.length === 2 ? '14px' : '12px', lineHeight: 1.2 }}>
                      {allocation ? teamName : ''}
                    </div>
                    {allocation?.duration && (
                      <div style={{ fontSize: sections.length === 1 ? '14px' : sections.length === 2 ? '12px' : '10px', opacity: 0.8, marginTop: sections.length === 1 ? '4px' : '2px' }}>
                        {allocation.duration}min
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Key */}
      <div style={{
        maxWidth: '1200px',
        margin: '16px auto 0',
        padding: '16px 20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '24px',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginRight: '8px' }}>Key:</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '28px', height: '28px',
            backgroundColor: '#ef4444',
            borderRadius: '4px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '13px', fontWeight: 'bold', flexShrink: 0
          }}>M</div>
          <span style={{ fontSize: '14px', color: '#374151' }}>Match</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '28px', height: '28px',
            backgroundColor: '#3b82f6',
            borderRadius: '4px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '13px', fontWeight: 'bold', flexShrink: 0
          }}>T</div>
          <span style={{ fontSize: '14px', color: '#374151' }}>Training</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '28px', height: '28px',
            border: '2.5px solid #f59e0b',
            borderRadius: '4px',
            outline: '1.5px solid #fde68a',
            outlineOffset: '1px',
            flexShrink: 0
          }} />
          <span style={{ fontSize: '14px', color: '#374151' }}>Your Team</span>
        </div>
      </div>

      {/* Sponsor */}
      {sponsorLogoUrl && (
        <div style={{
          maxWidth: '1200px',
          margin: '16px auto 0',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', fontWeight: '500' }}>
            Brought to you by our sponsor
          </div>
          <div 
            onClick={() => sponsorUrl && window.open(sponsorUrl, '_blank', 'noopener,noreferrer')} 
            style={{ cursor: sponsorUrl ? 'pointer' : 'default', display: 'flex', justifyContent: 'center' }}
          >
            <img 
              src={sponsorLogoUrl} 
              alt="Sponsor Logo" 
              loading="lazy" 
              style={{ 
                maxHeight: isMobile ? '60px' : '80px', 
                maxWidth: isMobile ? '200px' : '300px', 
                width: 'auto', 
                height: 'auto', 
                objectFit: 'contain' 
              }} 
              onError={e => e.currentTarget.parentElement.parentElement.style.display = 'none'} 
            />
          </div>
        </div>
      )}

      {/* CTA */}
      <div style={{
        maxWidth: '1200px', margin: '40px auto 20px',
        padding: isMobile ? '20px 16px' : '24px 32px',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #1e3a5f 100%)',
        borderRadius: '16px', textAlign: 'center'
      }}>
        <div style={{ fontSize: isMobile ? '14px' : '16px', color: 'rgba(255,255,255,0.9)', marginBottom: '8px', fontWeight: '500' }}>⚽ Love what you see?</div>
        <h3 style={{ fontSize: isMobile ? '20px' : '26px', fontWeight: '700', color: '#ffffff', margin: '0 0 12px 0' }}>Get your club on Pitchallo today</h3>
        <a href="https://pitchallo.app" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: isMobile ? '12px 24px' : '14px 32px', backgroundColor: '#22c55e', color: '#ffffff', fontSize: isMobile ? '15px' : '17px', fontWeight: '600', borderRadius: '50px', textDecoration: 'none' }}>
          Get Started Free <span>→</span>
        </a>
      </div>

      {/* Allocation detail popup */}
      {selectedAllocation && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }} onClick={() => setSelectedAllocation(null)}>
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, maxWidth: 320, width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setSelectedAllocation(null)} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
              <X size={20} />
            </button>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>{selectedAllocation.teamName}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Section {selectedAllocation.section} · {pitchName}</div>
            <div style={{ fontSize: 14, color: '#374151', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div>🕐 {fmtTime(selectedAllocation.startTime)} – {fmtTime(selectedAllocation.endTime)}</div>
              {selectedAllocation.opponent && <div>⚽ vs {selectedAllocation.opponent}</div>}
              {selectedAllocation.notes && <div>📝 {selectedAllocation.notes}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamScheduleView;
